import type { Profile, VendorListItem } from '@keboola/api-client/developerPortal/types';

/** Which gate, if any, stands between a signed-in user and the vendor dashboard. */
export type VendorGate = 'mfa' | 'select-vendor' | null;

type Input = {
  /** Approved vendors from the id token; pending `_v` ids already filtered out. */
  vendors: readonly string[];
  /**
   * `GET /auth/profile` value: `false` = not enrolled, else the factor name; `undefined` = loading.
   */
  mfa: Profile['mfa'] | undefined;
};

/**
 * ⚠️ Order is behaviour: MFA wins, else a vendor-less user gets the picker, then a wall on joining.
 *
 * - `mfa === undefined` (loading) never gates — no MFA flash for enrolled users.
 * - A failed profile read never reaches here; `routes/~.tsx` renders an error.
 * - `=== false`, not truthiness, lets the deprecated `'SMS_MFA'` factor through.
 */
export const resolveVendorGate = ({ vendors, mfa }: Input): VendorGate => {
  if (mfa === false) return 'mfa';
  if (vendors.length === 0) return 'select-vendor';
  return null;
};

/**
 * The joinable-vendor picker's options.
 *
 * - No filter: `GET /vendors` returns approved vendors only; `isApproved` is absent there, yet a
 *   filter on it type-checks via `VendorCommon`'s index signature.
 * - Sorted case-insensitively by name; the response is ordered by `id`.
 * - Copies first: `sort` would mutate TanStack Query's cached data.
 */
export const toVendorOptions = (
  vendors: readonly Pick<VendorListItem, 'id' | 'name'>[],
): { value: string; label: string }[] =>
  [...vendors]
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    .map((vendor) => ({ value: vendor.id, label: vendor.name }));
